import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { applyPageMeta, isBlogDetailPath, isAuthorPath } from '@/lib/pageMeta';
import {
  allResearchProjects,
  courseOfferings,
  internships,
  mediaEvents,
  workshops,
  conferenceEvents,
  lectureseries,
  lectureseriesDetails,
  guestlectures,
  booktalk,
  offerings,
  publications,
  reports
} from '@/data/mockData';

// Theme configurations
const THEMES = {
  crimson: { color: '#CC0033' },
  terracotta: { color: '#DB5527' },
  plum: { color: '#81204D' },
  teal: { color: '#0D9488' },
  amber: { color: '#D97706' },
  indigo: { color: '#4F46E5' },
  slate: { color: '#475569' }
};

interface MetaData {
  title: string;
  icon: string;
  color: string;
  description: string;
}

const getMetaForRoute = (path: string): MetaData => {
  // 1. DYNAMIC ROUTES
  
  // Research Project Detail
  if (path.startsWith('/research-projects/')) {
    const slug = path.substring('/research-projects/'.length);
    const project = allResearchProjects.find(p => p.slug === slug);
    return {
      title: project ? project.title : 'Research Project',
      icon: '📜',
      color: THEMES.terracotta.color,
      description: project ? project.description : 'Explore the research project conducted by the Centre for Human Sciences.'
    };
  }

  // Course Detail
  if (path.startsWith('/courses/')) {
    const slug = path.substring('/courses/'.length);
    const course = courseOfferings.find(c => c.slug === slug);
    return {
      title: course ? course.title : 'Course Details',
      icon: '🎓',
      color: THEMES.plum.color,
      description: course ? course.description : 'Explore the academic course offered by the Centre for Human Sciences.'
    };
  }

  // Internship Detail
  if (path.startsWith('/internships/')) {
    const slug = path.substring('/internships/'.length);
    const internship = internships.find(i => i.slug === slug);
    return {
      title: internship ? internship.title : 'Internship Details',
      icon: '💼',
      color: THEMES.teal.color,
      description: internship ? internship.description : 'Read about the internship opportunities at the Centre for Human Sciences.'
    };
  }

  if (isAuthorPath(path)) {
    return {
      title: 'Author',
      icon: '📝',
      color: THEMES.crimson.color,
      description: 'Read author profiles and articles from the Centre for Human Sciences.',
    };
  }

  // Blog Detail — meta tags are set by BlogDetail from the API
  if (isBlogDetailPath(path)) {
    return {
      title: 'Blog Article',
      icon: '📝',
      color: THEMES.crimson.color,
      description: 'Read insights and articles from the Centre for Human Sciences blog.',
    };
  }

  // Media Event Detail
  if (path.startsWith('/events/media/')) {
    const slug = path.substring('/events/media/'.length);
    const event = mediaEvents.find(e => e.slug === slug);
    return {
      title: event ? (event.fullHeading || event.title) : 'Media Coverage',
      icon: '📺',
      color: THEMES.plum.color,
      description: event ? (event.description || event.title) : 'Media coverage and updates from the Centre for Human Sciences.'
    };
  }

  // Workshop Detail
  if (path.startsWith('/events/workshop/')) {
    const slug = path.substring('/events/workshop/'.length);
    const workshop = workshops.find(w => w.slug === slug);
    return {
      title: workshop ? (workshop.heading || workshop.title) : 'Workshop Details',
      icon: '🛠️',
      color: THEMES.plum.color,
      description: workshop ? (workshop.description || workshop.title) : 'Learn more about workshops hosted by the Centre for Human Sciences.'
    };
  }

  // Conference Detail
  if (path.startsWith('/events/conference/')) {
    const slug = path.substring('/events/conference/'.length);
    const conference = conferenceEvents.find(c => c.slug === slug);
    return {
      title: conference ? (conference.heading || conference.title) : 'Conference Details',
      icon: '🏛️',
      color: THEMES.plum.color,
      description: conference ? (conference.description || conference.title) : 'Read details about conferences hosted by the Centre for Human Sciences.'
    };
  }

  // Lecture Series Detail
  if (path.startsWith('/lectures/lecture-series/')) {
    const slug = path.substring('/lectures/lecture-series/'.length);
    const details = lectureseriesDetails[slug];
    const meta = lectureseries.find(s => s.slug === slug);
    return {
      title: details ? details.title : (meta ? meta.title : 'Lecture Series'),
      icon: '🗣️',
      color: THEMES.amber.color,
      description: details ? (details.introduction || details.title) : 'Discover lecture series hosted by the Centre for Human Sciences.'
    };
  }

  // Guest Lecture Detail
  if (path.startsWith('/lectures/guest-lecture/')) {
    const slug = path.substring('/lectures/guest-lecture/'.length);
    const lecture = guestlectures.find(l => l.slug === slug);
    return {
      title: lecture ? lecture.title : 'Guest Lecture',
      icon: '🎤',
      color: THEMES.amber.color,
      description: lecture ? (lecture.description || `${lecture.speaker} speaks on ${lecture.title}`) : 'Guest lecture event hosted by the Centre for Human Sciences.'
    };
  }

  // Book Talk Detail
  if (path.startsWith('/lectures/book-talk/')) {
    const slug = path.substring('/lectures/book-talk/'.length);
    const talk = booktalk.find(t => t.slug === slug);
    return {
      title: talk ? talk.name : 'Book Talk',
      icon: '📖',
      color: THEMES.amber.color,
      description: talk ? (talk.description || `Book talk on ${talk.name} by ${talk.speaker}`) : 'Book talk event at the Centre for Human Sciences.'
    };
  }

  // Offering Detail
  if (path.startsWith('/offerings/')) {
    const slug = path.substring('/offerings/'.length);
    const offering = offerings.find(o => o.slug === slug);
    return {
      title: offering ? offering.title : 'Service Detail',
      icon: '🌟',
      color: THEMES.terracotta.color,
      description: offering ? (offering.description || offering.title) : 'Discover the offerings and services by the Centre for Human Sciences.'
    };
  }

  // Ebook Viewer Detail
  if (path.startsWith('/publications/ebooks/')) {
    const idStr = path.substring('/publications/ebooks/'.length);
    const id = parseInt(idStr, 10);
    const ebook = publications.ebooks.find(b => b.id === id);
    return {
      title: ebook ? `${ebook.name} (Ebook)` : 'Ebook Viewer',
      icon: '📖',
      color: THEMES.terracotta.color,
      description: ebook ? `Read online: ${ebook.name}. Published by the Centre for Human Sciences.` : 'Ebook viewer for publications.'
    };
  }

  // Event Flipbook Viewer
  if (path.startsWith('/publications/reports/events/')) {
    const idStr = path.substring('/publications/reports/events/'.length);
    const id = parseInt(idStr, 10);
    const event = reports.events.find(e => e.id === id);
    return {
      title: event ? `${event.title} (Report)` : 'Event Report Viewer',
      icon: '📊',
      color: THEMES.terracotta.color,
      description: event ? `Read online: ${event.title} event report. Published by the Centre for Human Sciences.` : 'Event report viewer.'
    };
  }

  // Annual Report Flipbook Viewer
  if (path.startsWith('/publications/reports/annual/')) {
    const idStr = path.substring('/publications/reports/annual/'.length);
    const id = parseInt(idStr, 10);
    const report = reports.annual.find(r => r.id === id);
    return {
      title: report ? `${report.title} (Report)` : 'Annual Report Viewer',
      icon: '📂',
      color: THEMES.terracotta.color,
      description: report ? `Read online: ${report.title}. Published by the Centre for Human Sciences.` : 'Annual report viewer.'
    };
  }

  // Exhibition Flipbook Viewer
  if (path.startsWith('/publications/exhibitions/')) {
    const idStr = path.substring('/publications/exhibitions/'.length);
    const id = parseInt(idStr, 10);
    const exhibition = publications.exhibitions.find(e => e.id === id);
    return {
      title: exhibition ? `${exhibition.title} (Exhibition)` : 'Exhibition Viewer',
      icon: '🎨',
      color: THEMES.terracotta.color,
      description: exhibition ? `Read online: ${exhibition.title} exhibition catalogue. Published by the Centre for Human Sciences.` : 'Exhibition catalogue viewer.'
    };
  }

  // 2. STATIC ROUTES
  switch (path) {
    case '/':
      return {
        title: 'Centre for Human Sciences',
        icon: '🌸',
        color: THEMES.crimson.color,
        description: 'Centre for Human Sciences (CHS) at Rishihood University is a research center dedicated to Indian Knowledge Systems, Indian philosophy, research projects, and academic offerings.'
      };
    
    // About Section
    case '/about':
    case '/about/chs':
      return {
        title: 'About CHS',
        icon: '🏛️',
        color: THEMES.crimson.color,
        description: 'Learn about the Centre for Human Sciences (CHS), our background, and our integration with Rishihood University.'
      };
    case '/about/rishihood':
      return {
        title: 'About Rishihood',
        icon: '🏛️',
        color: THEMES.crimson.color,
        description: 'Learn about Rishihood University, India\'s first social impact university based in Delhi NCR.'
      };
    case '/about/vision-mission':
      return {
        title: 'Vision & Mission',
        icon: '🎯',
        color: THEMES.crimson.color,
        description: 'Explore the vision, mission, and core values of the Centre for Human Sciences.'
      };
    case '/about/team':
      return {
        title: 'Our Team',
        icon: '👥',
        color: THEMES.crimson.color,
        description: 'Meet our dedicated team of researchers, educators, and administrators at the Centre for Human Sciences.'
      };
    case '/about/advisory-board':
      return {
        title: 'Advisory Board',
        icon: '🤝',
        color: THEMES.crimson.color,
        description: 'Meet the distinguished members of our Advisory Board who guide the Centre for Human Sciences.'
      };
    case '/about/research-advisory':
      return {
        title: 'Research Advisory',
        icon: '🧠',
        color: THEMES.crimson.color,
        description: 'Meet our Research Advisory board members overseeing our academic and publication research work.'
      };
    case '/about/internship-advisory':
      return {
        title: 'Internship Advisory',
        icon: '💼',
        color: THEMES.crimson.color,
        description: 'Meet the Internship Advisory board members supporting our experiential learning initiatives.'
      };
    case '/about/directors-note':
      return {
        title: "Director's Note",
        icon: '✉️',
        color: THEMES.crimson.color,
        description: 'Read the director\'s note detailing the aims, aspirations, and journey of the Centre for Human Sciences.'
      };

    // Research & Academic Section
    case '/research-projects':
      return {
        title: 'Research Projects',
        icon: '📜',
        color: THEMES.terracotta.color,
        description: 'Discover ongoing and completed research projects at the Centre for Human Sciences in Indian Knowledge Systems.'
      };
    case '/courses':
      return {
        title: 'Course Offerings',
        icon: '🎓',
        color: THEMES.plum.color,
        description: 'Browse the course offerings and academic courses provided by the Centre for Human Sciences.'
      };
    case '/internships':
      return {
        title: 'Internships',
        icon: '💼',
        color: THEMES.teal.color,
        description: 'Explore internship programs and experiential learning opportunities at the Centre for Human Sciences.'
      };
    case '/phd':
      return {
        title: 'PhD Programme',
        icon: '🖋️',
        color: THEMES.teal.color,
        description: 'Learn about the PhD Programme at the Centre for Human Sciences, focusing on Indian Knowledge Systems.'
      };

    // Publications
    case '/publications':
      return {
        title: 'Publications',
        icon: '📚',
        color: THEMES.terracotta.color,
        description: 'Browse books, journals, reports, and digital publications by the Centre for Human Sciences.'
      };
    case '/publications/books-and-journals':
      return {
        title: 'Books & Journals',
        icon: '📖',
        color: THEMES.terracotta.color,
        description: 'Explore books and academic journals published by the Centre for Human Sciences.'
      };
    case '/publications/ebooks':
      return {
        title: 'E-books',
        icon: '📱',
        color: THEMES.terracotta.color,
        description: 'Access digital e-books and virtual flipbooks published by the Centre for Human Sciences.'
      };
    case '/publications/exhibitions':
      return {
        title: 'Exhibitions',
        icon: '🎨',
        color: THEMES.terracotta.color,
        description: 'Browse exhibition catalogues and digital exhibition books by the Centre for Human Sciences.'
      };
    case '/publications/newsletter':
      return {
        title: 'Newsletters',
        icon: '📰',
        color: THEMES.terracotta.color,
        description: 'Read newsletters and periodical updates from the Centre for Human Sciences.'
      };
    case '/publications/reports':
    case '/publications/reports/project-reports':
      return {
        title: 'Project Reports',
        icon: '📊',
        color: THEMES.terracotta.color,
        description: 'Access project reports and research documents published by the Centre for Human Sciences.'
      };
    case '/publications/reports/events-and-conferences':
      return {
        title: 'Events & Conferences Reports',
        icon: '📝',
        color: THEMES.terracotta.color,
        description: 'Read report summaries and proceedings of events and conferences.'
      };
    case '/publications/reports/annual-report':
      return {
        title: 'Annual Reports',
        icon: '📂',
        color: THEMES.terracotta.color,
        description: 'Access our official annual reports highlighting the yearly accomplishments and milestones.'
      };

    // Blogs
    case '/blogs':
      return {
        title: 'Blogs & Insights',
        icon: '📝',
        color: THEMES.crimson.color,
        description: 'Read blogs, articles, insights, and opinion pieces from the Centre for Human Sciences.'
      };

    // Events
    case '/events':
      return {
        title: 'Events & Happenings',
        icon: '📅',
        color: THEMES.plum.color,
        description: 'Check upcoming and past events, conferences, workshops, and happenings at the Centre for Human Sciences.'
      };
    case '/events/conferences':
      return {
        title: 'Conferences & Seminars',
        icon: '🏛️',
        color: THEMES.plum.color,
        description: 'Discover conferences and seminars hosted by the Centre for Human Sciences.'
      };
    case '/events/centre':
      return {
        title: 'Centre Events',
        icon: '🌟',
        color: THEMES.plum.color,
        description: 'Explore internal and collaborative events hosted by the Centre for Human Sciences.'
      };
    case '/events/workshops':
      return {
        title: 'Workshops',
        icon: '🛠️',
        color: THEMES.plum.color,
        description: 'Explore practical workshops, trainings, and interactive sessions hosted by the Centre.'
      };
    case '/events/media':
      return {
        title: 'In The Media',
        icon: '📺',
        color: THEMES.plum.color,
        description: 'Browse media coverage, press releases, and newspaper clippings about the Centre.'
      };

    // Lectures
    case '/lectures':
    case '/lectures/lectureseries':
      return {
        title: 'Lecture Series',
        icon: '🗣️',
        color: THEMES.amber.color,
        description: 'Explore public lecture series and academic talks hosted by the Centre for Human Sciences.'
      };
    case '/lectures/guestlectures':
      return {
        title: 'Guest Lectures',
        icon: '🎤',
        color: THEMES.amber.color,
        description: 'Learn about guest lectures delivered by eminent scholars at the Centre.'
      };
    case '/lectures/booktalk':
      return {
        title: 'Book Talks',
        icon: '📖',
        color: THEMES.amber.color,
        description: 'Watch or read about Book Talks discussing prominent publications in human sciences.'
      };

    // Gallery, Offerings & Contact
    case '/media-gallery':
      return {
        title: 'Media Gallery',
        icon: '🖼️',
        color: THEMES.indigo.color,
        description: 'Explore photo and video galleries showcasing activities at the Centre for Human Sciences.'
      };
    case '/offerings':
      return {
        title: 'Our Offerings',
        icon: '🌟',
        color: THEMES.terracotta.color,
        description: 'Discover advisory services, applications, and educational solutions offered by the Centre.'
      };
    case '/contact':
      return {
        title: 'Contact Us',
        icon: '✉️',
        color: THEMES.slate.color,
        description: 'Get in touch with the Centre for Human Sciences at Rishihood University.'
      };

    default:
      return {
        title: 'Page Not Found',
        icon: '❓',
        color: THEMES.slate.color,
        description: 'Page not found - Centre for Human Sciences | Rishihood University.'
      };
  }
};

const PageMetaTracker = () => {
  const [location] = useLocation();

  useEffect(() => {
    if (isBlogDetailPath(location) || isAuthorPath(location)) {
      return;
    }

    const meta = getMetaForRoute(location);
    applyPageMeta(
      {
        title: meta.title,
        description: meta.description,
        url: `${window.location.origin}${location === '/' ? '' : location}`,
        image: `${window.location.origin}/favicon.svg`,
        type: 'website',
      },
      location,
    );
  }, [location]);

  return null;
};

export default PageMetaTracker;
